import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getAgencyOverview,
  createAgencyPortalSession,
} from "@/lib/agency.functions";
import { getStripeEnvironment } from "@/lib/stripe";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/agency/billing")({
  component: AgencyBillingPage,
});

function AgencyBillingPage() {
  const fetchOverview = useServerFn(getAgencyOverview);
  const openPortal = useServerFn(createAgencyPortalSession);
  const { data, isLoading } = useQuery({
    queryKey: ["agency-overview"],
    queryFn: () => fetchOverview(),
    staleTime: 30_000,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;

  const sub = data?.subscription;
  const balance = data?.balance ?? 0;

  const handlePortal = async () => {
    setErr(null);
    setBusy(true);
    try {
      const res = await openPortal({
        data: {
          environment: getStripeEnvironment(),
          return_origin: window.location.origin,
        },
      });
      if ("error" in res) throw new Error(res.error);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not open billing portal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-8">
      <header>
        <p
          className="text-[10px] font-medium uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          Billing
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold text-foreground">
          Plan & billing
        </h1>
      </header>

      <section className="grid gap-3 rounded-2xl border border-border bg-card/30 p-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Current plan
        </p>
        {!sub && (
          <>
            <p className="font-display text-2xl font-semibold text-foreground">
              No active plan
            </p>
            <p className="text-sm text-muted-foreground">
              You have {balance} credit{balance === 1 ? "" : "s"} available.
            </p>
            <div>
              <Link
                to="/agency-bundle"
                className="mt-3 inline-flex rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90"
                style={{ background: GOLD, color: "#0a0a0a" }}
              >
                Buy a bundle →
              </Link>
            </div>
          </>
        )}
        {sub && (
          <>
            <p className="font-display text-2xl font-semibold text-foreground">
              {sub.kind === "monthly"
                ? "Agency Monthly, $745 / month"
                : "Agency Bundle, $745 one-time"}
            </p>
            <p className="text-sm text-muted-foreground">
              Status: <span className="text-foreground">{sub.status}</span>
              {sub.kind === "monthly" && sub.current_period_end && (
                <>
                  {" "}
                  · {sub.cancel_at_period_end ? "Ends" : "Renews"}{" "}
                  {new Date(sub.current_period_end).toLocaleDateString()}
                </>
              )}
              {sub.kind === "one_time" && " · Credits valid 12 months from purchase"}
            </p>
            <p className="text-sm text-muted-foreground">
              Credit balance:{" "}
              <span className="font-medium text-foreground">{balance}</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              {sub.kind === "monthly" && (
                <button
                  onClick={handlePortal}
                  disabled={busy}
                  className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? "Opening…" : "Manage subscription →"}
                </button>
              )}
              <Link
                to="/agency-bundle"
                className="rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground hover:bg-card"
              >
                {sub.kind === "monthly" ? "Add a bundle" : "Buy another bundle"}
              </Link>
            </div>
          </>
        )}
        {err && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {err}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card/20 p-6 text-sm text-muted-foreground">
        <p>
          Invoices, payment method, and cancellations are managed in the
          billing portal. The portal opens in a new tab.
        </p>
      </section>
    </div>
  );
}